import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

const API_URL = import.meta.env.VITE_API_URL || ''

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
})

// Request interceptor - add auth token
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error: AxiosError) => {
        return Promise.reject(error)
    }
)

// Response interceptor - handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        if (error.response?.status === 401) {
            // Do not redirect if we are already on login or trying to login
            const url = error.config?.url || '';
            if (!url.includes('/auth/login') && !window.location.pathname.includes('/login')) {
                // Clear ALL localStorage to remove zustand persist state
                localStorage.clear()
                window.location.href = '/login'
            }
        }
        return Promise.reject(error)
    }
)

// Auth API
export const authApi = {
    login: (username: string, password: string) =>
        api.post('/auth/login', { username, password }),

    getMe: () => api.get('/auth/me'),

    changePassword: (oldPassword: string, newPassword: string) =>
        api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
}

// Operators API
export const operatorsApi = {
    getAll: (skip = 0, limit = 100) =>
        api.get(`/operators?skip=${skip}&limit=${limit}`),

    get: (id: number) => api.get(`/operators/${id}`),

    create: (data: { username: string; password: string; full_name?: string; role?: string }) =>
        api.post('/operators', data),

    update: (id: number, data: { username?: string; full_name?: string; role?: string; is_active?: boolean; password?: string }) =>
        api.put(`/operators/${id}`, data),

    delete: (id: number) => api.delete(`/operators/${id}`),

    changePassword: (id: number, newPassword: string) =>
        api.put(`/operators/${id}`, { password: newPassword }),

    uploadAvatar: (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post('/operators/me/avatar', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    updateProfile: (data: { full_name?: string; avatar_url?: string }) =>
        api.patch('/operators/me', data),
}

// TG Accounts API
export const accountsApi = {
    getAll: (skip = 0, limit = 100, includeInactive = false) =>
        api.get('/accounts', { params: { skip, limit, include_inactive: includeInactive } }),

    refreshStatuses: () => api.post('/accounts/refresh-statuses'),

    get: (id: number) => api.get(`/accounts/${id}`),

    create: (data: { phone: string; proxy_id?: number; assigned_operator_id?: number }) =>
        api.post('/accounts', data),

    update: (id: number, data: { proxy_id?: number; assigned_operator_id?: number }) =>
        api.patch(`/accounts/${id}`, data),

    delete: (id: number) => api.delete(`/accounts/${id}`),

    importTdata: (id: number, file: File) => {
        const formData = new FormData()
        formData.append('tdata_file', file)
        return api.post(`/accounts/${id}/import-tdata`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    connect: (id: number) => api.post(`/accounts/${id}/connect`),

    disconnect: (id: number) => api.post(`/accounts/${id}/disconnect`),

    createFromTdata: (file: File, proxyId?: number, operatorId?: number) => {
        const formData = new FormData()
        formData.append('tdata_file', file)
        if (proxyId) formData.append('proxy_id', proxyId.toString())
        if (operatorId) formData.append('assigned_operator_id', operatorId.toString())
        return api.post('/accounts/create-from-tdata', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    // Phone Authorization Flow
    sendCode: (id: number) => api.post(`/accounts/${id}/send-code`),

    resendCode: (id: number, phoneCodeHash: string) =>
        api.post(`/accounts/${id}/resend-code`, null, {
            params: { phone_code_hash: phoneCodeHash }
        }),

    verifyCode: (id: number, code: string, phoneCodeHash: string) =>
        api.post(`/accounts/${id}/verify-code`, null, {
            params: { code, phone_code_hash: phoneCodeHash }
        }),

    verify2fa: (id: number, password: string) =>
        api.post(`/accounts/${id}/verify-2fa`, null, {
            params: { password }
        }),

    // QR Code Authorization Flow
    startQrLogin: (proxyId?: number, operatorId?: number) =>
        api.post('/accounts/qr-login/start', null, {
            params: { proxy_id: proxyId, assigned_operator_id: operatorId }
        }),

    checkQrLogin: (id: number) =>
        api.post(`/accounts/${id}/check-qr-login`),

    qr2fa: (id: number, password: string) =>
        api.post(`/accounts/${id}/qr-2fa`, null, {
            params: { password }
        }),

    // Chat/Messaging
    getFolders: (id: number) =>
        api.get(`/accounts/${id}/folders`),

    getDialogs: (id: number, limit = 0, folderId?: number, offsetDate = 0, offsetId = 0, offsetPeerId?: number) =>
        api.get(`/accounts/${id}/dialogs`, { params: { limit, folder_id: folderId, offset_date: offsetDate, offset_id: offsetId, offset_peer_id: offsetPeerId } }),

    getTopics: (id: number, chatId: number, limit = 100) =>
        api.get(`/accounts/${id}/topics/${chatId}`, { params: { limit } }),

    getMessages: (id: number, chatId: number, limit = 50, skip = 0, topicId?: number) =>
        api.get(`/accounts/${id}/messages/${chatId}`, { params: { limit, skip, topic_id: topicId } }),

    sendMessage: (id: number, chatId: number, text: string, replyToMsgId?: number, topicId?: number) =>
        api.post(`/accounts/${id}/send/${chatId}`, null, { params: { text, reply_to_msg_id: replyToMsgId, topic_id: topicId } }),

    sendMedia: (id: number, chatId: number, file: File, caption?: string, topicId?: number, replyToMsgId?: number) => {
        const formData = new FormData()
        formData.append('file', file)
        if (caption) formData.append('caption', caption)
        if (topicId !== undefined) formData.append('topic_id', String(topicId))
        if (replyToMsgId !== undefined) formData.append('reply_to_msg_id', String(replyToMsgId))
        return api.post(`/accounts/${id}/send-media/${chatId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    sendToNewContact: (id: number, username?: string, phone?: string, text?: string) =>
        api.post(`/accounts/${id}/send-new`, null, { params: { username, phone, text } }),

    deleteChat: (id: number, chatId: number, deleteInTelegram = false) =>
        api.delete(`/accounts/${id}/delete-chat/${chatId}`, { params: { delete_in_telegram: deleteInTelegram } }),

    markAsRead: (id: number, chatId: number, topicId?: number, maxMessageId?: number) =>
        api.post(`/accounts/${id}/mark-read/${chatId}`, null, { params: { topic_id: topicId, max_message_id: maxMessageId } }),

    blockUser: (id: number, chatId: number) =>
        api.post(`/accounts/${id}/block-user/${chatId}`),
    unblockUser: (id: number, chatId: number) =>
        api.post(`/accounts/${id}/unblock-user/${chatId}`),

    // Закреплённые сообщения
    getPinnedMessages: (id: number, chatId: number, topicId?: number) =>
        api.get(`/accounts/${id}/pinned/${chatId}`, { params: { topic_id: topicId } }),
    pinMessage: (id: number, chatId: number, messageId: number) =>
        api.post(`/accounts/${id}/pin/${chatId}/${messageId}`),
    unpinMessage: (id: number, chatId: number, messageId: number) =>
        api.post(`/accounts/${id}/unpin/${chatId}/${messageId}`),

    // Удаление и пересылка сообщений
    editMessage: (id: number, chatId: number, messageId: number, text: string) =>
        api.post(`/accounts/${id}/edit-message/${chatId}/${messageId}`, null, { params: { text } }),
    deleteMessages: (id: number, chatId: number, messageIds: number[], revoke = true) =>
        api.post(`/accounts/${id}/delete-messages/${chatId}`, messageIds, { params: { revoke } }),
    forwardMessages: (id: number, fromChatId: number, toChatIds: number[], messageIds: number[]) =>
        api.post(`/accounts/${id}/forward-messages`, { from_chat_id: fromChatId, to_chat_ids: toChatIds, message_ids: messageIds }),

    // Поиск пользователя Telegram по username или телефону
    searchUser: (id: number, query: string) =>
        api.get(`/accounts/${id}/search-user`, { params: { query } }),

    // Фоновая синхронизация аватарок
    syncAvatars: (id: number, chatIds: number[]) =>
        api.post(`/accounts/${id}/sync-avatars`, chatIds),

    checkProxy: (id: number) => api.get<{ success: boolean, message: string, ip?: string, proxy?: string }>(`/accounts/${id}/check-proxy`),
}

// Chats API
export const chatsApi = {
    getAll: (skip = 0, limit = 50, archived = false) =>
        api.get(`/chats?skip=${skip}&limit=${limit}&archived=${archived}`),

    get: (id: number) => api.get(`/chats/${id}`),

    getMessages: (chatId: number, skip = 0, limit = 50) =>
        api.get(`/chats/${chatId}/messages?skip=${skip}&limit=${limit}`),

    sendMessage: (chatId: number, text: string) =>
        api.post(`/chats/${chatId}/messages`, { chat_id: chatId, text_content: text }),

    markAsRead: (chatId: number) => api.post(`/chats/${chatId}/read`),

    archive: (chatId: number) => api.post(`/chats/${chatId}/archive`),

    unarchive: (chatId: number) => api.post(`/chats/${chatId}/unarchive`),

    exportExcel: (all: boolean = true, ids?: number[]) =>
        api.get('/chats/export/excel', {
            params: { all, ids: ids?.join(',') },
            responseType: 'blob'
        }),
}

// Contacts API
export const contactsApi = {
    getAll: (skip = 0, limit = 50, search?: string, tag?: string) => {
        let url = `/contacts?skip=${skip}&limit=${limit}`
        if (search) url += `&search=${encodeURIComponent(search)}`
        if (tag) url += `&tag=${encodeURIComponent(tag)}`
        return api.get(url)
    },

    get: (id: number) => api.get(`/contacts/${id}`),

    create: (data: { username?: string; phone?: string; first_name?: string; last_name?: string; notes?: string; tags?: string[] }) =>
        api.post('/contacts', data),

    update: (id: number, data: { tags?: string[]; notes?: string; is_blocked?: boolean; first_name?: string; last_name?: string }) =>
        api.put(`/contacts/${id}`, data),

    delete: (id: number) => api.delete(`/contacts/${id}`),

    addTag: (id: number, tag: string) =>
        api.post(`/contacts/${id}/add-tag?tag=${encodeURIComponent(tag)}`),

    removeTag: (id: number, tag: string) =>
        api.post(`/contacts/${id}/remove-tag?tag=${encodeURIComponent(tag)}`),

    block: (id: number) => api.post(`/contacts/${id}/block`),

    unblock: (id: number) => api.post(`/contacts/${id}/unblock`),

    getAllTags: () => api.get('/contacts/tags/list'),
}

// Proxies API
export const proxiesApi = {
    getAll: (skip = 0, limit = 100) =>
        api.get(`/proxies?skip=${skip}&limit=${limit}`),

    get: (id: number) => api.get(`/proxies/${id}`),

    create: (data: {
        name: string
        type: string
        host: string
        port: number
        username?: string
        password?: string
        country?: string
        is_mobile?: boolean
    }) => api.post('/proxies', data),

    update: (id: number, data: { name?: string; host?: string; port?: number; status?: string }) =>
        api.put(`/proxies/${id}`, data),

    delete: (id: number) => api.delete(`/proxies/${id}`),

    check: (id: number) => api.post(`/proxies/${id}/check`),
}

// Notifications API
export const notificationsApi = {
    getAll: (params?: any) => api.get('/notifications', { params }),
    markRead: (id: number) => api.post(`/notifications/${id}/read`),
    markAllRead: () => api.post('/notifications/read-all'),
    getUnreadCount: () => api.get('/notifications/unread-count'),
}

// Internal Chats API
export const internalChatsApi = {
    getMyChats: () => api.get('/internal/chats'),
    startChat: (recipientId: number) => api.post(`/internal/chats?recipient_id=${recipientId}`),
    getMessages: (chatId: number, params?: any) => api.get(`/internal/chats/${chatId}/messages`, { params }),
    sendMessage: (data: { recipient_id: number, text_content: string }) => api.post('/internal/chats/messages', data),
    getUnreadCount: () => api.get('/internal/chats/unread-count'),
}

// Stats API
export const statsApi = {
    getDashboard: () => api.get('/stats/dashboard'),

    getOperators: () => api.get('/stats/operators'),

    getAccounts: () => api.get('/stats/accounts'),

    getMyStats: () => api.get('/stats/my-stats'),
}

export default api
